import React from "react";

import uniqBy from "lodash/uniqBy";
import filter from "lodash/filter";

import {
  getCategoryLinkQueryFromFilterOptions,
  getDefaultUrlType,
} from "@helpers/plasmic/catalog-entries";

import LinkWithLang from "@components/swagmagic/dynamic/slices/client-link-with-lang";
import InfoCard, {
  INFO_BANNER_CARD_TYPE,
  INFO_CATEGORY_CARD_TYPE,
  InfoCardType,
} from "@components/swagmagic/dynamic/cards/category/info-card";
import { CatalogEntriesProps } from "@components/swagmagic/dynamic/slices/catalog-entries";

import { computeSpans, GRID_CONFIG, getHeaderButtonLinkDataAndType } from "@helpers/plasmic/slices";
import { CATEGORY_PRODUCT_LISTING_LINK_TYPE } from "@stadium/shared/src/constants/stores/routes";

import styles from "./main.module.scss";

export type Banner = {
  image: string | { url?: string };
  imageMobile?: string | { url?: string };
  imageTablet?: string | { url?: string };
  urlType: string;
  filterOptions?: string;
  linkToNewTab?: boolean;
  otherUrl: string;
  code?: string;
  title: string;
  span: number;
  spanTablet: number;
  spanMobile: number;
  position: number;
};

export interface CatalogEntriesGridProps extends CatalogEntriesProps {
  children?: React.ReactNode;
  categories?: any[];
  spans?: {
    columns: number;
    tabletColumns: number;
    mobileColumns: number;
  };
  banners?: Banner[];
}

export type ComputedSpans = {
  colSpan: number;
  tabletSpan: number;
  mobileSpan: number;
};

type CSSVarStyle = React.CSSProperties & Record<string, string | number | undefined>;
type GridCard = {
  title: string;
  src?: string;
  srcMobile?: string;
  srcTablet?: string;
  code?: string;
  urlType?: string;
  filterOptions?: string;
  linkToNewTab?: boolean;
  otherUrl?: string;
  type: InfoCardType;
  colSpan?: number;
  tabletSpan?: number;
  mobileSpan?: number;
};

const TABLET_ASPECT_WIDTHS: Record<number, number> = {
  1: 160,
  2: 336,
  3: 512,
  4: 688,
};

const MOBILE_ASPECT_WIDTHS: Record<number, number> = {
  1: 171,
  2: 358,
};

const DESKTOP_ASPECT_WIDTHS: Record<number, number> = {
  1: 255.5,
  4: 1082,
};

const createBannerCard = (banner: Banner): any => ({
  title: banner.title,
  src: (typeof banner.image === "object" && banner.image?.url) || banner.image,
  srcMobile:
    (typeof banner.imageMobile === "object" && banner.imageMobile?.url) || banner.imageMobile,
  srcTablet:
    (typeof banner.imageTablet === "object" && banner.imageTablet?.url) || banner.imageTablet,
  urlType: banner.urlType,
  filterOptions: banner.filterOptions,
  linkToNewTab: banner.linkToNewTab ?? false,
  otherUrl: banner.otherUrl,
  type: INFO_BANNER_CARD_TYPE,
  code: banner.code,
  colSpan: banner.span ?? 1,
  tabletSpan: banner.spanTablet ?? 1,
  mobileSpan: banner.spanMobile ?? 1,
});

const CatalogEntriesGrid = ({
  className,
  title,
  description,
  variant,
  categorySlug,
  spans,
  categories = [],
  banners = [],
  children = null,
  linkRouteDefaults,
}: CatalogEntriesGridProps) => {
  const { columns, tabletColumns, mobileColumns } = spans ?? GRID_CONFIG[variant];
  const distinctBannersByPosition = uniqBy(
    filter(banners, (banner) => Boolean(banner.title)),
    "position",
  );

  const gridStyle: CSSVarStyle = {
    "--columns": String(columns),
    "--columns-tablet": String(tabletColumns ?? Math.min(columns, 6)),
    "--columns-mobile": String(mobileColumns),
  };

  const renderCards = () => {
    const categoryCards: GridCard[] = categories.map((category) => {
      const imageUrl = category.portraitImage?.url || category.portraitImage;
      const imageMobileUrl = category.imageMobile?.url || category.imageMobile;
      const imageTabletUrl = category.imageTablet?.url || category.imageTablet;

      return {
        title: category.name,
        src: imageUrl,
        srcMobile: imageMobileUrl,
        srcTablet: imageTabletUrl,
        code: category.code,
        urlType:
          category.children?.length === 0 ? CATEGORY_PRODUCT_LISTING_LINK_TYPE : category.urlType,
        filterOptions: category.filterOptions,
        linkToNewTab: category.linkToNewTab ?? false,
        otherUrl: category.otherUrl,
        type: INFO_CATEGORY_CARD_TYPE,
      };
    });

    const cardsToRender: GridCard[] = [...categoryCards];

    if (distinctBannersByPosition?.length) {
      const bannerCardsWithPosition = distinctBannersByPosition.map((banner) => ({
        card: createBannerCard(banner),
        position: banner.position,
      }));

      // Sort banners by position in ascending order to insert from left to right
      const sortedBanners = [...bannerCardsWithPosition];
      sortedBanners.sort((a, b) => a.position - b.position);

      sortedBanners.forEach(({ card, position }) => {
        const insertIndex = Math.max(0, Math.min(position, cardsToRender.length));
        cardsToRender.splice(insertIndex, 0, card);
      });
    }

    return cardsToRender.map((card, index) => {
      const { colSpan, tabletSpan, mobileSpan } = computeSpans(
        card,
        columns,
        tabletColumns ?? Math.min(columns, 6),
        mobileColumns,
      );

      const desktopAspectWidth = DESKTOP_ASPECT_WIDTHS[colSpan];

      const spanStyle: CSSVarStyle = {
        "--col-span": String(colSpan),
        "--col-span-tablet": String(tabletSpan),
        "--col-span-mobile": String(mobileSpan),
        "--tablet-aspect-width": String(TABLET_ASPECT_WIDTHS[tabletSpan] ?? 160),
        "--mobile-aspect-width": String(MOBILE_ASPECT_WIDTHS[mobileSpan] ?? 171),
        ...(desktopAspectWidth ? { "--desktop-aspect-ratio": `${desktopAspectWidth} / 308` } : {}),
      };

      const cardBody = (
        <InfoCard
          src={card.src}
          srcMobile={card.srcMobile}
          srcTablet={card.srcTablet}
          title={card.title}
          variant={variant}
          type={card.type}
          isMultiSpan={colSpan > 1 || tabletSpan > 1 || mobileSpan > 1}
        />
      );

      const linkDataAndType = getHeaderButtonLinkDataAndType({
        linkType: card.urlType || getDefaultUrlType(categorySlug),
        otherUrl: card.otherUrl,
        categorySlug: card.code,
      });

      return (
        <LinkWithLang
          {...linkRouteDefaults}
          {...linkDataAndType}
          query={getCategoryLinkQueryFromFilterOptions(card.filterOptions)}
          key={card.code ?? card.title ?? `card-${index}`}
          target={card.linkToNewTab ? "_blank" : "_self"}
          className={styles.cardLink}
          style={spanStyle}
        >
          {cardBody}
        </LinkWithLang>
      );
    });
  };

  const descriptionToShow = description?.trim();
  const titleToShow = title?.trim();
  const hasVisibleHeaderContent = Boolean(titleToShow) || Boolean(descriptionToShow);

  return (
    <div className={`${styles.wrapper} ${className ?? ""}`.trim()}>
      {hasVisibleHeaderContent && (
        <div className={styles.headerContainer}>
          {titleToShow && <div className={styles.sliceTitle}>{titleToShow}</div>}
          {descriptionToShow && (
            <div className={styles.descriptionContainer}>{descriptionToShow}</div>
          )}
        </div>
      )}
      <div className={styles.categoryListWrapper}>
        {children}
        <section className={styles.gridWrapper}>
          <div className={styles.grid} style={gridStyle}>
            {renderCards()}
          </div>
        </section>
      </div>
    </div>
  );
};

export default CatalogEntriesGrid;
